import React, { useState, useRef } from 'react';
import axios from 'axios';
import './Profile.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, BugReportModal, FeedbackModal, Button } from './shared';
import { useLogout, useUserData, useScrollRestoration } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AdBanner } from './ads';
import { formatUserDisplayName } from '../utils'; // Issue #415

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
// Issue #185: Beta mode flag for bug reporting feature
const BETA_MODE = process.env.REACT_APP_BETA_MODE === 'true';

function Profile() {
  const navigation = useNavigation();
  const { userData, refetch: refreshUserData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  // Issue #374 (follow-up): Preserve scroll position when leaving Profile to
  // open one of the policy documents, so the user returns to the same spot.
  const saveProfileScrollPosition = useScrollRestoration('profileScrollPosition');
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  // Issue #84: Local state to immediately display uploaded photo
  const [localPhotoUrl, setLocalPhotoUrl] = useState(null);
  // Issue #185: Bug report modal state (beta only)
  const [showBugReportModal, setShowBugReportModal] = useState(false);
  const [isSubmittingBugReport, setIsSubmittingBugReport] = useState(false);
  // Issue #232: Feedback modal state (beta only)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Issue #415: Append "(Beta)" to the displayed name for beta test accounts
  // (@universe-app.online) so testers can recognize they are using a test
  // account. The email is rendered as-is.
  const fullName = formatUserDisplayName(userData);
  const email = userData?.email || '';
  // Use local photo URL if available, otherwise use userData photo URL
  const profilePhotoUrl = localPhotoUrl || userData?.profile_photo_url;

  // Issue #84: Handle profile photo upload
  const handleProfilePhotoClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('profile.invalidFileType'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('profile.fileTooLarge'));
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await axios.post(`${API_BASE_URL}/api/user/profile-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      if (response.data.success && response.data.profile_photo_url) {
        // Immediately display the uploaded photo
        setLocalPhotoUrl(response.data.profile_photo_url);
        // Also refresh user data for consistency
        if (refreshUserData) {
          refreshUserData();
        }
      }
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
      alert(error.response?.data?.message || t('profile.uploadError'));
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };
  // Issue #185: Handle bug report submission
  // Issue #337: Changed to multipart/form-data for screenshot upload
  const handleBugReportSubmit = async (bugReport) => {
    setIsSubmittingBugReport(true);
    try {
      const formData = new FormData();
      formData.append('steps', JSON.stringify(bugReport.steps));
      if (bugReport.expected_result) formData.append('expected_result', bugReport.expected_result);
      if (bugReport.actual_result) formData.append('actual_result', bugReport.actual_result);
      if (bugReport.connection_quality) formData.append('connection_quality', bugReport.connection_quality);
      if (bugReport.os) formData.append('os', bugReport.os);
      if (bugReport.screenshot) formData.append('screenshot', bugReport.screenshot);

      const response = await axios.post(`${API_BASE_URL}/api/bug-report`, formData, {
        withCredentials: true,
      });

      if (response.data.success) {
        alert(t('bugReport.submitSuccess'));
        setShowBugReportModal(false);
      }
    } catch (error) {
      console.error('Failed to submit bug report:', error);
      alert(error.response?.data?.message || t('bugReport.submitError'));
      throw error; // Re-throw to prevent clearing draft in modal
    } finally {
      setIsSubmittingBugReport(false);
    }
  };

  // Issue #232: Handle feedback submission
  const handleFeedbackSubmit = async (feedback) => {
    setIsSubmittingFeedback(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/feedback`, feedback, {
        withCredentials: true,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Feedback submission failed');
      }
      // Success - FeedbackModal will show thank you message and auto-close
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert(error.response?.data?.message || t('feedback.submitError') || 'Fehler beim Senden des Feedbacks');
      throw error; // Re-throw to prevent showing thank you in modal
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <div className="profile-page">
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation */}
      <div className="tab-navigation-profile">
        <span className="tab-title">{t('profile.title')}</span>
      </div>

      {/* Profile Content */}
      <main className="profile-content">
        {/* Account Information Section - Issue #84: Clickable profile photo - Issue #115: Improved accessibility */}
        <div className="account-info-section">
          <button
            type="button"
            className={`profile-icon clickable ${isUploading ? 'uploading' : ''}`}
            onClick={handleProfilePhotoClick}
            aria-label={t('profile.changePhoto') || 'Profilfoto ändern'}
            disabled={isUploading}
          >
            {profilePhotoUrl ? (
              <img
                src={`${API_BASE_URL}${profilePhotoUrl}`}
                alt={t('profile.photoAlt') || 'Profilfoto'}
                className="profile-photo"
              />
            ) : (
              <svg className="profile-avatar-icon" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="18" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
                <path d="M6 30C6 24.4772 10.4772 20 16 20H20C25.5228 20 30 24.4772 30 30" stroke="currentColor" strokeWidth="2"/>
                <circle cx="18" cy="18" r="15" stroke="currentColor" strokeWidth="2"/>
              </svg>
            )}
            {isUploading && <div className="upload-spinner" aria-hidden="true"></div>}
            <div className="photo-edit-overlay" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="13" r="4" stroke="white" strokeWidth="2"/>
              </svg>
            </div>
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            aria-label={t('profile.selectPhoto') || 'Foto auswählen'}
          />
          <div className="account-info-text">
            <span className="user-full-name">{fullName}</span>
            <span className="user-email">{email}</span>
          </div>
        </div>

        {/* Issue #311: Ad Banner positioned between profile info and beta section */}
        <AdBanner slot="profile-info" />

        {/* Issue #185: Bug Report Button (Beta Only) */}
        {/* Issue #232: Feedback Button (Beta Only) */}
        {BETA_MODE && (
          <nav className="menu-section" aria-labelledby="beta-section-title">
            <h2 className="menu-section-title" id="beta-section-title">{t('bugReport.beta')}</h2>
            <button className="menu-item-row" onClick={() => setShowBugReportModal(true)} type="button">
              <span className="menu-item-icon" aria-hidden="true">&#128030;</span>
              <span className="menu-item-text">{t('bugReport.title')}</span>
            </button>
            <button className="menu-item-row" onClick={() => setShowFeedbackModal(true)} type="button">
              <span className="menu-item-icon" aria-hidden="true">&#128172;</span>
              <span className="menu-item-text">{t('feedback.title') || 'Feedback senden'}</span>
            </button>
          </nav>
        )}

        {/* Konto Section - Issue #115: Changed menu items to buttons for accessibility */}
        <nav className="menu-section" aria-labelledby="account-section-title">
          <h2 className="menu-section-title" id="account-section-title">{t('profile.account')}</h2>
          <button className="menu-item-row" onClick={navigation.onNavigateToChangePassword} type="button">
            <span className="menu-item-icon" aria-hidden="true">&#128274;</span>
            <span className="menu-item-text">{t('profile.changePassword')}</span>
          </button>
          {/* Issue #137: Kontoverwaltung - Account settings */}
          {/* Issue #169: Changed 2D gear icon to 3D emoji for design consistency */}
          <button className="menu-item-row" onClick={navigation.onNavigateToAccountSettings} type="button">
            <span className="menu-item-icon" aria-hidden="true">&#128736;</span>
            <span className="menu-item-text">{t('profile.accountSettings')}</span>
          </button>
        </nav>

        {/* App-Einstellungen Section - Issue #115: Changed menu items to buttons for accessibility */}
        <nav className="menu-section" aria-labelledby="app-settings-section-title">
          <h2 className="menu-section-title" id="app-settings-section-title">{t('profile.appSettings')}</h2>
          <button className="menu-item-row" onClick={navigation.onNavigateToNotificationSettings} type="button">
            <span className="menu-item-icon" aria-hidden="true">&#128276;</span>
            <span className="menu-item-text">{t('profile.notifications')}</span>
          </button>
          <button className="menu-item-row" onClick={navigation.onNavigateToLanguageSettings} type="button">
            <span className="menu-item-icon" aria-hidden="true">&#127760;</span>
            <span className="menu-item-text">{t('profile.language')}</span>
          </button>
          <button className="menu-item-row" onClick={navigation.onNavigateToDisplaySettings} type="button">
            <span className="menu-item-icon" aria-hidden="true">&#128241;</span>
            <span className="menu-item-text">{t('profile.display')}</span>
          </button>
        </nav>

        {/* Datenschutz & Richtlinien Section - Issue #115: Changed menu items to buttons for accessibility */}
        <nav className="menu-section" aria-labelledby="privacy-section-title">
          <h2 className="menu-section-title" id="privacy-section-title">{t('profile.privacy')}</h2>
          {/* Issue #118: Terms and Conditions - enabled */}
          <button
            className="menu-item-row"
            onClick={() => { saveProfileScrollPosition(); navigation.onNavigateToTermsAndConditions(); }}
            type="button"
          >
            <span className="menu-item-icon" aria-hidden="true">&#128220;</span>
            <span className="menu-item-text">{t('profile.terms')}</span>
          </button>
          {/* Issue #68: Privacy Policy - enabled */}
          <button
            className="menu-item-row"
            onClick={() => { saveProfileScrollPosition(); navigation.onNavigateToPrivacyPolicy(); }}
            type="button"
          >
            <span className="menu-item-icon" aria-hidden="true">&#128272;</span>
            <span className="menu-item-text">{t('profile.privacyPolicy')}</span>
          </button>
          {/* Issue #149: Community Guidelines - enabled */}
          <button
            className="menu-item-row"
            onClick={() => { saveProfileScrollPosition(); navigation.onNavigateToCommunityGuideline(); }}
            type="button"
          >
            <span className="menu-item-icon" aria-hidden="true">&#128214;</span>
            <span className="menu-item-text">{t('profile.communityGuidelines')}</span>
          </button>
        </nav>

        {/* Logout Button */}
        <div className="logout-button-container">
          <Button
            variant="secondary"
            fullWidth
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? t('nav.loggingOut') : t('nav.logout')}
          </Button>
        </div>
      </main>

      <BottomNavigation
        activeTab="profile"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />

      {/* Issue #185: Bug Report Modal (Beta Only) */}
      {BETA_MODE && (
        <BugReportModal
          isOpen={showBugReportModal}
          onClose={() => setShowBugReportModal(false)}
          onSubmit={handleBugReportSubmit}
          isSubmitting={isSubmittingBugReport}
        />
      )}

      {/* Issue #232: Feedback Modal (Beta Only) */}
      {BETA_MODE && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
          isSubmitting={isSubmittingFeedback}
        />
      )}
    </div>
  );
}

export default Profile;
